import PictureImage from '../Common/PictureImage';
import { IMAGE_ALT } from '../../seo/imageAltText';
import React, { useEffect, useRef, type ReactNode } from "react";
import { Link } from "react-router-dom";
import "./Tiktokfulfilmentcontent.css";
import { useSectionInView, useStaggerReveal } from "../../hooks/useInViewOnce";

const ext = (href: string, label: string): ReactNode => (
  <a href={href} target="_blank" rel="noopener noreferrer nofollow">
    {label}
  </a>
);
/* ─── Icons ─────────────────────────────────────────────── */
const TikTokLogo = () => (
  <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
    <path d="M15.5 1.5C15.5 1.5 16 5.5 19.5 6.8V10.5C19.5 10.5 17 10.5 15.5 9V15.5C15.5 18.54 13.04 21 10 21C6.96 21 4.5 18.54 4.5 15.5C4.5 12.46 6.96 10 10 10V13.6C8.73 13.6 7.7 14.63 7.7 15.9C7.7 17.17 8.73 18.2 10 18.2C11.27 18.2 12.3 17.17 12.3 15.9V1.5H15.5Z" fill="currentColor"/>
  </svg>
);

const ArrowRight = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <path d="M3 9H15M10 4.5L15 9L10 13.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const Check = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <circle cx="10" cy="10" r="10" fill="#28479e" opacity="0.12"/>
    <path d="M6 10L8.8 13L14 7" stroke="#28479e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const BoltIcon = () => (
  <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
    <path d="M17 3L6 17H14L13 27L24 13H16L17 3Z" fill="#28479e" opacity="0.15" stroke="#28479e" strokeWidth="1.8" strokeLinejoin="round"/>
  </svg>
);

const TrackIcon = () => (
  <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
    <circle cx="15" cy="15" r="11" fill="#28479e" opacity="0.1" stroke="#28479e" strokeWidth="1.8"/>
    <circle cx="15" cy="15" r="4.5" fill="#28479e" opacity="0.2" stroke="#28479e" strokeWidth="1.6"/>
    <circle cx="15" cy="15" r="1.8" fill="#28479e"/>
    <path d="M15 4V8M15 22V26M4 15H8M22 15H26" stroke="#28479e" strokeWidth="1.8" strokeLinecap="round"/>
  </svg>
);

const ShipFastIcon = () => (
  <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
    <rect x="3" y="11" width="18" height="12" rx="2" fill="#28479e" opacity="0.1" stroke="#28479e" strokeWidth="1.8"/>
    <path d="M21 15H27L29 19V23H21V15Z" fill="#28479e" opacity="0.1" stroke="#28479e" strokeWidth="1.8" strokeLinejoin="round"/>
    <circle cx="9" cy="24.5" r="2.5" fill="white" stroke="#28479e" strokeWidth="1.6"/>
    <circle cx="24" cy="24.5" r="2.5" fill="white" stroke="#28479e" strokeWidth="1.6"/>
    <path d="M3 15H21" stroke="#28479e" strokeWidth="1.6"/>
  </svg>
);

const AutoIcon = () => (
  <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
    <rect x="5" y="4" width="20" height="22" rx="3" fill="#28479e" opacity="0.1" stroke="#28479e" strokeWidth="1.8"/>
    <path d="M10 11H20M10 16H16M10 21H13" stroke="#28479e" strokeWidth="1.8" strokeLinecap="round"/>
    <circle cx="22" cy="22" r="5" fill="#28479e"/>
    <path d="M19.5 22L21.2 23.8L24.5 20.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ScaleIcon = () => (
  <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
    <path d="M15 5L27 23H3L15 5Z" fill="#28479e" opacity="0.1" stroke="#28479e" strokeWidth="1.8" strokeLinejoin="round"/>
    <path d="M15 10V18" stroke="#28479e" strokeWidth="2" strokeLinecap="round"/>
    <circle cx="15" cy="20.5" r="1.5" fill="#28479e"/>
  </svg>
);

const SupportIcon = () => (
  <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
    <circle cx="15" cy="15" r="11" fill="#28479e" opacity="0.1" stroke="#28479e" strokeWidth="1.8"/>
    <path d="M10 13C10 10.24 12.24 8 15 8C17.76 8 20 10.24 20 13" stroke="#28479e" strokeWidth="1.8" strokeLinecap="round"/>
    <rect x="7" y="13" width="3.5" height="6" rx="1.5" fill="#28479e" opacity="0.2" stroke="#28479e" strokeWidth="1.5"/>
    <rect x="19.5" y="13" width="3.5" height="6" rx="1.5" fill="#28479e" opacity="0.2" stroke="#28479e" strokeWidth="1.5"/>
    <path d="M23 19C23 21 21 22.5 19 22.5H16" stroke="#28479e" strokeWidth="1.6" strokeLinecap="round"/>
  </svg>
);



// Icons for Handling Viral Volume section
const LightningIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
    <path d="M13 2L3 15H10L9 24L19 11H12L13 2Z" fill="white" stroke="white" strokeWidth="1.5" strokeLinejoin="round" />
  </svg>
);

const PackageIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
    <rect x="3" y="8" width="18" height="12" rx="2" fill="white" />
    <path d="M3 8L12 3L21 8" stroke="white" strokeWidth="2" strokeLinejoin="round" />
    <path d="M12 3V17" stroke="white" strokeWidth="2" />
    <path d="M3 8L12 13L21 8" stroke="white" strokeWidth="2" />
  </svg>
);

const SyncIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="8" stroke="white" strokeWidth="2" fill="none" />
    <path d="M8 12C8 9.79 9.79 8 12 8C13.41 8 14.63 8.75 15.32 9.84" stroke="white" strokeWidth="2" strokeLinecap="round" />
    <path d="M16 12C16 14.21 14.21 16 12 16C10.59 16 9.37 15.25 8.68 14.16" stroke="white" strokeWidth="2" strokeLinecap="round" />
    <path d="M15 8L17 10L15 12" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M9 14L7 16L9 18" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ClockIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="9" stroke="white" strokeWidth="2" fill="none" />
    <circle cx="12" cy="12" r="1.5" fill="white" />
    <path d="M12 8V12L16 14" stroke="white" strokeWidth="2" strokeLinecap="round" />
  </svg>
);



/* ─── Data ──────────────────────────────────────────────── */
const whyFeatures = [
  {
    icon: <AutoIcon />,
    title: "Automated Order Fulfilment",
    desc: "TikTok Shop orders sync automatically to our fulfilment system, enabling fast, accurate order processing without manual intervention.",
  },
  {
    icon: <TrackIcon />,
    title: "Real-Time Inventory Management",
    desc: "Track every SKU with real-time inventory updates, helping prevent overselling and keeping product availability accurate across sales channels.",
  },
  {
    icon: <ShipFastIcon />,
    title: "Fast UK Delivery",
    desc: (
      <>
        Same-day dispatch and trusted courier partnerships including Royal Mail,{' '}
        {ext('https://www.dpd.co.uk/', 'DPD')} and {ext('https://www.evri.com/business', 'Evri')} support reliable next-day
        and two-day delivery across the UK via our{' '}
        <Link to="/logistics-services" className="tiktok-inline-link">e-commerce logistics for sellers</Link>.
      </>
    ),
  },
  {
    icon: <BoltIcon />,
    title: "Built for Viral Sales",
    desc: "Our scalable fulfilment operation handles sudden TikTok Shop order spikes without compromising speed, accuracy, or customer satisfaction.",
  },
  {
    icon: <ScaleIcon />,
    title: "Scalable Fulfilment",
    desc: "From startup brands to high-volume sellers, our fulfilment services grow alongside your TikTok Shop business.",
  },
  {
    icon: <SupportIcon />,
    title: "Dedicated UK Support",
    desc: "Work with experienced fulfilment specialists who understand TikTok Shop operations and provide responsive support whenever required.",
  },
];

const integrationFeatures = [
  "Automatic TikTok Shop order import for instant fulfilment processing.",
  "Real-time inventory synchronisation across TikTok Shop and connected sales channels.",
  "Automatic tracking updates pushed directly to TikTok Shop after dispatch.",
  "Multi-SKU products and variant support for accurate order fulfilment.",
  "High-volume order processing designed for viral TikTok Shop sales.",
  "TikTok Shop integration completed in under 24 business hours.",
];

const viralVolumeFeatures = [
  {
    icon: <LightningIcon />,
    title: "Live Order Spikes",
    desc: "Scalable fulfilment for viral TikTok Shop sales and sudden order surges.",
  },
  {
    icon: <PackageIcon />,
    title: "Fast Pick & Pack",
    desc: "Barcode-driven pick and pack fulfilment with consistent order accuracy.",
  },
  {
    icon: <SyncIcon />,
    title: "Real-Time Inventory Sync",
    desc: "Live inventory synchronisation keeps TikTok Shop stock levels accurate.",
  },
  {
    icon: <ClockIcon />,
    title: "Rapid Dispatch",
    desc: "Same-day dispatch helps meet customer expectations and delivery targets.",
  },
];


/* ─── Component ─────────────────────────────────────────── */
const TikTokFulfilment: React.FC = () => {
  const { refs: whyRefs, visible: whyVisible } = useStaggerReveal(whyFeatures.length, 0.1, 100);
  const { ref: intRef, inView: intVisible } = useSectionInView(0.15);
  const { refs: viralRefs, visible: viralVisible } = useStaggerReveal(viralVolumeFeatures.length, 0.1, 100);
//   const { refs: tiktokBenRefs, visible: tiktokBenVisible } = useStagger(tiktokBenefits.length);
  
  // Mobile slider refs for auto-scroll
  const viralSliderRef = useRef<HTMLDivElement>(null);
  const whySliderRef = useRef<HTMLDivElement>(null);
  const viralAutoScrollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const whyAutoScrollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Auto-scroll functionality for mobile viral volume section
  useEffect(() => {
    const slider = viralSliderRef.current;
    if (!slider) return;

    const isMobile = window.innerWidth <= 768;
    if (!isMobile) return;

    let currentIndex = 0;
    const getCardWidth = () => {
      const containerWidth = slider.offsetWidth;
      return containerWidth * 0.85; // 85% width for each card
    };
    const gap = 16;

    const scrollToCard = (index: number) => {
      if (!slider) return;
      const cardWidth = getCardWidth();
      const scrollPosition = index * (cardWidth + gap);
      slider.scrollTo({
        left: scrollPosition,
        behavior: 'smooth'
      });
    };

    const autoScroll = () => {
      currentIndex = (currentIndex + 1) % viralVolumeFeatures.length;
      scrollToCard(currentIndex);
    };

    // Start auto-scroll after initial delay
    const startAutoScroll = setTimeout(() => {
      viralAutoScrollIntervalRef.current = setInterval(autoScroll, 4000); // Scroll every 4 seconds
    }, 2000);

    // Cleanup
    return () => {
      clearTimeout(startAutoScroll);
      if (viralAutoScrollIntervalRef.current) {
        clearInterval(viralAutoScrollIntervalRef.current);
      }
    };
  }, []);

  // Auto-scroll functionality for mobile why choose us section
  useEffect(() => {
    const slider = whySliderRef.current;
    if (!slider) return;

    const isMobile = window.innerWidth <= 768;
    if (!isMobile) return;

    let currentIndex = 0;
    const getCardWidth = () => {
      const containerWidth = slider.offsetWidth;
      return containerWidth * 0.85; // 85% width for each card
    };
    const gap = 16;

    const scrollToCard = (index: number) => {
      if (!slider) return;
      const cardWidth = getCardWidth();
      const scrollPosition = index * (cardWidth + gap);
      slider.scrollTo({
        left: scrollPosition,
        behavior: 'smooth'
      });
    };

    const autoScroll = () => {
      currentIndex = (currentIndex + 1) % whyFeatures.length;
      scrollToCard(currentIndex);
    };

    // Start auto-scroll after initial delay
    const startAutoScroll = setTimeout(() => {
      whyAutoScrollIntervalRef.current = setInterval(autoScroll, 4000); // Scroll every 4 seconds
    }, 2000);

    // Cleanup
    return () => {
      clearTimeout(startAutoScroll);
      if (whyAutoScrollIntervalRef.current) {
        clearInterval(whyAutoScrollIntervalRef.current);
      }
    };
  }, []);

  return (
    <div className="ttf-root">

   
       

      {/* ══ HANDLING VIRAL VOLUME ══════════════════════════════════════ */}
      <section className="ttf-viral-section ttf-section--genz">
        <div className="ttf-viral-wrapper">
          <div className="ttf-viral-container">
            <div className="ttf-sec-head">
              <span className="ttf-genz-pill">Built for viral moments</span>
              <h2 className="ttf-h2">TikTok Shop Fulfilment Built for Viral Order Growth</h2>
              <p className="ttf-sec-p">
                Our TikTok Shop fulfilment services scale with demand, keeping inventory accurate and orders moving
                during every viral sales surge — including{' '}
                <Link to="/tiktok-live-sales-fulfillment" className="tiktok-inline-link">
                  TikTok Live sales fulfilment
                </Link>{' '}
                spikes. Seller performance rules in{' '}
                {ext('https://seller-uk.tiktok.com/university/home', 'TikTok Shop University')} explain why dispatch
                speed and tracking matter for UK sellers.
              </p>
            </div>
            
            {/* Desktop Grid */}
            <div className="ttf-viral-grid">
              {viralVolumeFeatures.map((f, i) => (
                <div
                  key={i}
                  ref={el => { viralRefs.current[i] = el; }}
                  className={`ttf-viral-card ${viralVisible[i] ? "ttf-anim--in" : "ttf-anim--out"}`}
                  style={{ transitionDelay: `${i * 80}ms` }}
                >
                  <div className="ttf-viral-icon">{f.icon}</div>
                  <h3 className="ttf-viral-card-title">{f.title}</h3>
                  <p className="ttf-viral-card-desc">{f.desc}</p>
                </div>
              ))}
            </div>

            {/* Mobile Slider */}
            <div className="ttf-viral-mobile-slider" ref={viralSliderRef}>
              {viralVolumeFeatures.map((f, i) => (
                <div key={i} className="ttf-viral-mobile-slide">
                  <div className="ttf-viral-mobile-card">
                    <div className="ttf-viral-icon">{f.icon}</div>
                    <p className="ttf-viral-card-title">{f.title}</p>
                    <p className="ttf-viral-card-desc">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

     

      {/* ══ WHY CHOOSE ══════════════════════════════════════ */}
      <section className="ttf-section ttf-section--white">
        <div className="ttf-container">
          <div className="ttf-sec-head">
            <span className="ttf-tag">Why Choose Us</span>
            <h2 className="ttf-h2">Why TikTok Shop Sellers Choose Pick Pack Pro</h2>
            <p className="ttf-sec-p">
              Our TikTok Shop fulfilment services help growing ecommerce brands automate order fulfilment, maintain
              accurate inventory, and deliver fast shipping across the UK, giving you the confidence to scale sales
              without operational bottlenecks. Many brands combine TikTok with{' '}
              <Link to="/multi-channel-fulfillment-services" className="tiktok-inline-link">
                multi-channel fulfilment UK
              </Link>{' '}
              so one stock pool serves every storefront.
            </p>
          </div>
          
          {/* Desktop Grid */}
          <div className="ttf-grid-3">
            {whyFeatures.map((f, i) => (
              <div
                key={i}
                ref={el => { whyRefs.current[i] = el; }}
                className={`ttf-feat-card ${whyVisible[i] ? "ttf-anim--in" : "ttf-anim--out"}`}
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div className="ttf-feat-icon">{f.icon}</div>
                <h3 className="ttf-feat-title">{f.title}</h3>
                <p className="ttf-feat-desc">{f.desc}</p>
              </div>
            ))}
          </div>

          {/* Mobile Slider */}
          <div className="ttf-why-mobile-slider" ref={whySliderRef}>
            {whyFeatures.map((f, i) => (
              <div key={i} className="ttf-why-mobile-slide">
                <div className="ttf-why-mobile-card">
                  <div className="ttf-feat-icon">{f.icon}</div>
                  <p className="ttf-feat-title">{f.title}</p>
                  <p className="ttf-feat-desc">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

    
      {/* ══ BENEFITS ════════════════════════════════════════ */}
      <section className="ttf-section ttf-section--blue ttf-section--benefits-compact">
        <div className="ttf-container">
          <div className="ttf-benefits-layout ttf-benefits-layout--intro">
            <div className="ttf-benefits-left">
              <span className="ttf-tag ttf-tag--white-outline">Seller Benefits</span>
              <h2 className="ttf-h2 ttf-h2--white">Key Benefits of TikTok Shop Fulfilment Services UK</h2>
              <p className="ttf-sec-p ttf-sec-p--on-blue">
                Our TikTok Shop fulfilment services help UK ecommerce brands reduce operational workload, improve
                delivery performance, and scale confidently while we manage storage, picking, packing, and shipping.
                Returns are handled through our{' '}
                <Link to="/return-handling-services" className="tiktok-inline-link tiktok-inline-link--on-blue">
                  returns handling services
                </Link>
                , and international parcels can route via {ext('https://www.dhl.com/gb-en', 'DHL UK')} when required.
              </p>
              <Link to="/contact-fulfillment-services" className="ttf-btn ttf-btn--white" style={{ marginTop: "8px" }}>
                Get started <ArrowRight />
              </Link>
            </div>
          </div>
        </div>
      </section>

   
      {/* ══ INTEGRATION ═════════════════════════════════════ */}
      <section className="ttf-section ttf-section--white">
        <div
          className="ttf-container"
          ref={intRef}
        >
          <div className={`ttf-integration-layout ${intVisible ? "ttf-anim--in" : "ttf-anim--out"}`}>
            <div className="ttf-integration-left">
              <PictureImage width={700} height={525}
                src="/ticktock/TickTock.jpg"
                alt={IMAGE_ALT.tiktokIntegration}
                className="ttf-integration-img" loading="lazy" />
              <div className="ttf-integration-badge">
                <div className="ttf-integration-badge-icon"><TikTokLogo /></div>
                <div>
                  <span className="ttf-integration-badge-title">TikTok Shop</span>
                  <span className="ttf-integration-badge-sub">Connected & syncing</span>
                </div>
                <div className="ttf-integration-dot" />
              </div>
            </div>
            <div className="ttf-integration-right">
              <span className="ttf-tag">Integration</span>
              <h2 className="ttf-h2">Easy TikTok Shop Integration with Your Fulfilment Operations</h2>
              <p className="ttf-sec-p" style={{ textAlign: "left" }}>
                Connect your TikTok Shop to our fulfilment platform in under 24 hours. Orders, inventory, and tracking
                information sync automatically, reducing manual work and improving fulfilment accuracy through our{' '}
                <Link to="/integrations" className="tiktok-inline-link">
                  ecommerce integrations
                </Link>{' '}
                and {ext('https://www.mintsoft.com/', 'Mintsoft WMS')} portal.
              </p>
              <ul className="ttf-integration-list">
                {integrationFeatures.map((f, i) => (
                  <li key={i}><Check /><span>{f}</span></li>
                ))}
              </ul>
              <p className="ttf-sec-p" style={{ textAlign: "left", marginTop: "18px" }}>
                Inventory can also sync with{' '}
                <Link to="/shopify-fulfillment-services" className="tiktok-inline-link">
                  Shopify fulfilment UK
                </Link>{' '}
                and other channels via {ext('https://www.shopify.com/uk', 'Shopify UK')} when you sell beyond TikTok.
              </p>
              <Link to="/contact-fulfillment-services" className="ttf-btn ttf-btn--solid" style={{ marginTop: "28px" }}>
                Connect your store <ArrowRight />
              </Link>
            </div>
          </div>
        </div>
      </section>


    </div>
  );
};

export default TikTokFulfilment;
